/**
 * One ranked job on the dashboard: scores, analysis, A/B tag,
 * interested/not-interested, and "Tailor CV" (PIVOT-110, `POST
 * /api/apply/cv`). The saved-page equivalent is `MatchListRow`, which also
 * carries the candidate's own application-status log (PIVOT-112) — this
 * component has no application history to show for a job scored today, so it
 * never took those props.
 *
 * Tailoring is driven end to end by this component, the same way
 * `ConsentGate`/`CvUpload` own their one endpoint (frontend/CLAUDE.md §2):
 * it calls the backend, then navigates to the review page with the draft it
 * got back. Nothing is persisted at this point — `ApplyReview` is what
 * offers a save, and only a save writes anything.
 *
 * PIVOT-142: two disclosure/rhythm changes on top of the above.
 *
 * 1. The analysis is disclosure, not truncation — `.match-card-analysis`
 *    still has no server-side truncation and never will (see MatchCard.css).
 *    What changed is *display*: a long analysis clamps to ~4 lines behind a
 *    fade, with a one-way "show full reasoning" toggle that reveals the rest
 *    in place. The full text is in the DOM either way (a screen reader
 *    always gets all of it) — the toggle only affects what a sighted reader
 *    sees before clicking. `isLongAnalysis` decides purely from string
 *    length, not from any layout measurement, on purpose: `scrollHeight` vs
 *    `clientHeight` is the "real" signal but reads as 0/0 in jsdom, which
 *    would make the toggle untestable — see MatchCard.test.tsx. The
 *    threshold is picked low, biased toward the narrowest card tier, so the
 *    control shows whenever the CSS clamp *could* plausibly be cutting
 *    something short — an occasionally-unneeded toggle beats a caveat with
 *    no visible way to reach it (constitution §I).
 *
 *    A first pass here clamped the whole string by a fixed line count and
 *    trusted that real analyses always put their caveat early enough to
 *    survive 4 lines. Reading real "scores" rows disproved that: ~1 in 6
 *    analyses where salary wasn't scored carry "— salary undisclosed, not
 *    scored" (or similar) as a trailing clause of the *last* sentence, and a
 *    fixed 4-line clamp lands mid-sentence before it on a card long enough.
 *
 *    A second pass fixed that one shape by always keeping the literal last
 *    sentence outside the clamp — and was itself disproved the same way: a
 *    full sweep of all 163 real `scores` rows with `salary_score IS NULL AND
 *    disqualified = false` found the caveat sentence followed by ANOTHER
 *    trailing sentence (a tag-rationale or "worth checking" aside) in 16 of
 *    them, so "keep only the last sentence" left the caveat itself inside
 *    the still-clamped head on exactly those rows. `findProtectFrom` below
 *    fixes the actual shape of the problem: it finds the caveat sentence by
 *    what it says (a "salary" clause paired with a qualifier the scoring
 *    prompt is instructed to use — see `rank.py`'s system prompt, "salary
 *    undisclosed, not scored" — and paraphrases the same sweep turned up,
 *    e.g. "scoring is moot"), wherever it sits, and holds back *that*
 *    sentence plus everything after it, not a fixed position. This is a
 *    structural guarantee anchored on what the sentence actually says, not a
 *    proxy for it — it does not ask whether `salaryScore === null` (the same
 *    proxy-signal mistake this repo's CLAUDE.md warns against by example:
 *    `profile_complete`, `dry_day`); a card whose salary wasn't scored for
 *    some other reason, or whose caveat uses wording the regex misses, still
 *    falls back to protecting the last sentence, same as before. A single
 *    run-on sentence with nothing to split off skips clamping entirely
 *    rather than risk cutting inside its own only sentence, and a caveat
 *    sentence with nothing before it (no head to clamp at all) does the
 *    same.
 *
 *    A third pass fixed the caveat detector's actual reach: the regex only
 *    understood English wording, but the analysis is trilingual — `rank.py`'s
 *    system prompt writes independent French and Dutch versions of "the same
 *    judgment" too, only ever giving the judge an English example phrase, so
 *    each language paraphrases the caveat on its own ("salaire non communiqué,
 *    non noté", "salaris niet vermeld, niet gescoord"). An English-only regex
 *    against fr/nl text simply never matches, which falls back to "protect
 *    only the last sentence" — the exact first-pass defect shape — and a real
 *    Neon sweep of all three `analysis` columns on the same 163 rows found it
 *    firing in 14/163 fr rows and 14/163 nl rows (the caveat sentence followed
 *    by a further trailing sentence, same shape the second pass fixed for
 *    English only). `SALARY_CAVEAT_PATTERNS` below is one detector per
 *    analysis locale, picked by `resolveAnalysisLocale` — the locale the
 *    rendered text actually is, which the UI's active `locale` does not
 *    always match (see `analysisFor`'s fallback chain). Fixing this also
 *    exposed a real, independent bug in `splitSentences`: its old
 *    regex-based split silently DROPPED the text between two non-terminator
 *    periods inside one "sentence" whenever a decimal number or abbreviation
 *    (a collective-bargaining scale like "CP329.02", "éch.4.1") sat between
 *    them, because `[^.!?]+` cannot itself consume a literal `.` and the
 *    lookahead only accepted one directly followed by whitespace/end. On a
 *    real row that dropped "CP329." outright and, worse, split "salaire ACS"
 *    away from the "non chiffré, donc non noté" that named the actual
 *    caveat — putting the caveat sentence in a fragment the salary-word
 *    regex could never see, regardless of language. `splitSentences` is now
 *    a manual scan rather than one regex, and only ever advances past a
 *    `.`/`!`/`?` as a sentence break when it is followed by whitespace or the
 *    end of the string — every other character, decimal points included, is
 *    part of the sentence body. Re-verified against all 163×3 real rows:
 *    `sentences.join('') === text` now holds with zero exceptions (it did
 *    not, before).
 * 2. `featured` (from `Dashboard.tsx`, the top-ranked card of each list)
 *    spans two grid columns and sets the title at `--fs-3` instead of the
 *    element-level h3 size — one piece of rhythm instead of eleven equal
 *    cards. The analysis stays `--fs-sm`; only the heading grows.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ApiError } from '../api/client'
import { tailorCv, writeCoverLetter } from '../api/apply'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import { isHttpUrl } from '../lib/url'
import type { Analysis, ArtifactKind, Interest, Locale, Match } from '../types/domain'
import { Icon } from './Icon'
import { ScoreBadges } from './ScoreBadges'
import { Thinking } from './Thinking'
import './MatchCard.css'
import '../styles/job-posting-link.css'

/** What `MatchCard` hands the review page on a successful draft — nothing
 * saved yet, so this travels as router state rather than an id. `kind`
 * (PIVOT-111) is what lets one review page (`pages/ApplyReview.tsx`) handle
 * both actions below instead of forking into two near-identical pages. */
export interface ApplyReviewState {
  jobId: string
  jobTitle: string
  jobOrg: string
  draft: string
  language: Locale
  kind: ArtifactKind
}

/** Maps a generation call's status code to actionable copy, shared between
 * "Tailor CV" and "Write cover letter" (PIVOT-111) -- both draw on the same
 * consent/budget gates, so only the no-saved-CV wording differs by action. */
function applyErrorKey(reason: unknown, noCvKey: TranslationKey): TranslationKey {
  if (reason instanceof ApiError) {
    if (reason.status === 403) return 'apply.errorConsent'
    if (reason.status === 400) return noCvKey
    if (reason.status === 429) return 'apply.errorBudget'
  }
  return 'common.error'
}

/** Which key of `analysis` `analysisFor` is actually about to render — the
 * fallback chain means that is not always `locale` itself (a score written
 * before PIVOT-142's trilingual judge carries English only, so a French or
 * Dutch reader still gets `analysis.en`). `findProtectFrom` needs this, not
 * `locale`, to pick the caveat pattern that matches the TEXT ON SCREEN —
 * matching against the reader's UI language while the text itself is in a
 * different one is exactly the kind of proxy-for-a-fact mistake this repo's
 * CLAUDE.md warns against. */
function resolveAnalysisLocale(analysis: Analysis, locale: Locale): Locale {
  if (analysis[locale]) return locale
  if (analysis.fr) return 'fr'
  if (analysis.nl) return 'nl'
  return 'en'
}

/** The analysis in the chosen language, or the one language the score has.
 *
 * A score written under PIVOT-159 carries exactly one language — the
 * candidate's own at scoring time — so the header's switch stays instant for
 * the interface while the analysis itself does not move; a row written
 * before that carries all three, and the switch really did resolve
 * instantly for it. Either way this never translates: it only ever selects
 * among the languages the row already has. */
function analysisFor(analysis: Analysis, locale: Locale): string {
  return analysis[resolveAnalysisLocale(analysis, locale)] ?? ''
}

/** `analysisLanguageNote`'s `{{language}}`, named in the reader's own UI
 * language — never the bare locale code, and never the language's name for
 * itself (a French reader sees "néerlandais", not "Nederlands"). */
const ANALYSIS_LANGUAGE_KEYS: Record<Locale, TranslationKey> = {
  fr: 'dashboard.analysisLanguageFr',
  nl: 'dashboard.analysisLanguageNl',
  en: 'dashboard.analysisLanguageEn',
}

/** Below this many characters, a 4-line clamp has nothing to hide — see the
 * module docstring for why this is a character count, not a measured height. */
const ANALYSIS_CLAMP_CHARS = 170

function isLongAnalysis(text: string): boolean {
  return text.length > ANALYSIS_CLAMP_CHARS
}

/** Splits an analysis into its sentences, terminator and trailing whitespace
 * kept with the sentence they end — so `sentences.join('')` reproduces the
 * input exactly, character for character. That exactness is what lets the
 * render below cut the string into "everything before the protected
 * sentence" and "the protected sentence onward" and be sure nothing in
 * between was dropped or duplicated.
 *
 * A `.`/`!`/`?` only ends a sentence when it is followed by whitespace or
 * the end of the string — anything else (a decimal collective-bargaining
 * scale like "CP329.02", an abbreviation like "éch.4.1") is just a character
 * inside the sentence body, same as a letter. This used to be one regex
 * (`/[^.!?]+[.!?]*(?:\s+|$)/g`), which got this case wrong in a way that
 * silently dropped text rather than just misplacing a boundary: `[^.!?]+`
 * cannot itself consume a literal `.`, so when a mid-sentence period was NOT
 * followed by whitespace (the `.` inside "329.02"), neither the body class
 * nor the optional terminator class could include it, and the global match
 * skipped straight past it to the next character a valid match COULD start
 * from — the `.` and everything the engine had to backtrack over to reach
 * that next start point vanished from every fragment. A manual scan doesn't
 * have that failure mode: every character is assigned to exactly one
 * fragment, so the join-equals-input invariant this function has always
 * claimed is now actually true (confirmed against all 163×3 real analyses
 * across every locale — see the module docstring's third-pass note). This
 * one real row is what losing the text actually did: "...salaire ACS (CP" +
 * [dropped: "329."] + "02 éch.4.1) non chiffré, donc non noté" — split into
 * a fragment ending at "salaire ACS" and a separate one starting "02 éch...",
 * so the fragment carrying the actual caveat wording ("non chiffré, donc non
 * noté") no longer even contained the word "salaire" for the caveat regex to
 * anchor on. */
function splitSentences(text: string): string[] {
  const sentences: string[] = []
  let start = 0
  let i = 0
  while (i < text.length) {
    if (text[i] === '.' || text[i] === '!' || text[i] === '?') {
      // A run of terminators ("?!", "...") ends together, not one sentence
      // per punctuation mark.
      let j = i + 1
      while (j < text.length && /[.!?]/.test(text[j])) j += 1
      if (j >= text.length || /\s/.test(text[j])) {
        // A real sentence break: fold the run of whitespace after it into
        // this sentence too, same as the old regex's trailing `(?:\s+|$)`.
        let k = j
        while (k < text.length && /\s/.test(text[k])) k += 1
        sentences.push(text.slice(start, k))
        start = k
        i = k
        continue
      }
      // Not a break (e.g. the "." in "329.02") — treat it as an ordinary
      // character and keep scanning the same sentence.
      i = j
      continue
    }
    i += 1
  }
  if (start < text.length) sentences.push(text.slice(start))
  return sentences.length > 0 ? sentences : [text]
}

/** Matches a sentence that states the salary-scoring caveat the judge prompt
 * is instructed to write (`backend/src/pivot/llm/prompts/rank.py`: "If the
 * job posting does not disclose a salary ... say so plainly in your
 * analysis, e.g. 'salary undisclosed, not scored'"), one pattern per
 * `Analysis` locale — the prompt gives the judge only an English example
 * phrase and tells it to write "the same judgment" in French and Dutch too,
 * so each language settles on its own wording rather than a translation of
 * the English one.
 *
 * Each pattern requires the salary word AND a qualifier in the SAME
 * sentence, so an unrelated qualifier elsewhere in the analysis (the values
 * axis gets its own "non noté"/"niet gescoord" in a few real rows when the
 * candidate stated no values preference) doesn't trip it on its own — and
 * if it ever does anyway, in a sentence that also happens to hold the real
 * salary mention, the effect is to protect more than strictly necessary,
 * never less (see `findProtectFrom`). The qualifier lists are the
 * instructed English phrase plus every paraphrase a full sweep of all 163
 * real `scores` rows with `salary_score IS NULL AND disqualified = false`
 * turned up, across all three `analysis` columns (see module docstring) —
 * not exhaustive of every wording an LLM could produce, which is why
 * `findProtectFrom` below still falls back to protecting the last sentence
 * when nothing here matches. */
const SALARY_CAVEAT_PATTERNS: Record<Locale, RegExp> = {
  en: /salary.*(?:not scored|unscored|undisclosed|not disclosed|left unscored|wasn't scored|isn't scored|scoring is moot)/i,
  // "salaire"/"rémunération" + a negation ("non"/"pas", up to two words away
  // to survive "n'est pas encore communiqué"-shaped phrasing) next to one of
  // the participles/adjectives the real rows actually pair it with —
  // "salaire non communiqué, non noté" and its paraphrases.
  fr: /(?:salaire|rémunération).*\b(?:non|pas)\b(?:\s+\S+){0,2}?\s*(?:noté|notée|évalué|évaluée|scoré|scorée|évaluer|communiqué|communiquée|divulgué|divulguée|précisé|précisée|indiqué|indiquée|chiffré|chiffrée|renseigné|renseignée|mentionné|mentionnée|pertinent)/i,
  // "salaris"/"loon" (word-boundaried so it doesn't fire inside
  // "loonbeheerder"/"loonschaal") + "niet"/"geen" next to the matching
  // participle, or the one-word "ongescoord".
  nl: /(?:salaris|loon\b).*(?:(?:niet|geen)\b(?:\s+\S+){0,2}?\s*(?:vermeld|meegedeeld|bekendgemaakt|gescoord|beoordeeld|bekend|relevant|gegeven|gekend)|ongescoord)/i,
}

/** The index of the sentence that must never be clamped or hidden, plus
 * every sentence after it — or -1 when there is nothing to specifically
 * protect (a single-sentence analysis, handled by the caller).
 *
 * `analysisLocale` must be the locale of the TEXT being scanned — the key
 * `analysisFor` actually returned (`resolveAnalysisLocale`), not necessarily
 * the reader's UI `locale` (see that function's docstring for why they can
 * differ). Passing the wrong one silently regresses to the exact bug this
 * function exists to fix: an English pattern can't see a French caveat.
 *
 * Prefers the sentence that actually states the salary caveat
 * (`SALARY_CAVEAT_PATTERNS[analysisLocale]`), wherever it sits — the bug
 * this exists to fix is that the caveat is not reliably the last sentence
 * (see module docstring). Falls back to the literal last sentence when no
 * caveat is detected, as a general defense for whatever qualifying wording
 * the pattern above doesn't cover — cheaper than clamping a sentence we
 * have no specific reason to trust is safe to hide. */
function findProtectFrom(sentences: string[], analysisLocale: Locale): number {
  const pattern = SALARY_CAVEAT_PATTERNS[analysisLocale]
  const caveatIndex = sentences.findIndex((s) => pattern.test(s))
  return caveatIndex !== -1 ? caveatIndex : sentences.length - 1
}

interface MatchCardProps {
  match: Match
  onInterest: (jobId: string, interest: Interest) => void | Promise<void>
  /** "This job no longer exists" (PIVOT-166) — a signal, never a delete;
   * the job stays on this slate until the next fetch regardless of outcome. */
  onReportGone: (jobId: string) => void | Promise<void>
  /** Set by `Dashboard.tsx` on the top-ranked card of each list (PIVOT-142) —
   * one piece of rhythm rather than a grid of equals. Defaults to `false` so
   * every other call site (and every existing test) is unaffected. */
  featured?: boolean
  /** PIVOT-133: the caller's last `onInterest` for this job failed and has
   * already reverted the card's own `interest` — this only adds the visible
   * reason. A flag, not a message, per frontend/CLAUDE.md §7: `t('common.error')`
   * translates it at render. */
  interestFailed?: boolean
  /** Optimistically true once this card's report has been sent and has not
   * failed (PIVOT-166, PIVOT-133 pattern) — the button reads as "Reported"
   * and stops being clickable again for this session. */
  reportedGone?: boolean
  /** The caller's last report for this job failed and has already reverted
   * `reportedGone` — this only adds the visible reason. */
  reportGoneFailed?: boolean
}

export function MatchCard({
  match,
  onInterest,
  onReportGone,
  featured = false,
  interestFailed = false,
  reportedGone = false,
  reportGoneFailed = false,
}: MatchCardProps) {
  const { locale, t } = useI18n()
  const navigate = useNavigate()
  // A `Set`, not a single `Interest | null`: the sibling button is left
  // enabled-but-inert (PIVOT-131), so a user can click "Interested" then
  // change their mind and click "Not interested" before the first request
  // settles. A single shared value can't represent both in flight at once —
  // the second click would overwrite the first and the first button would
  // read as no-longer-busy while its own request was still outstanding.
  const [pending, setPending] = useState<ReadonlySet<Interest>>(new Set())
  const [tailoring, setTailoring] = useState(false)
  const [tailorError, setTailorError] = useState<TranslationKey | null>(null)
  const [writingLetter, setWritingLetter] = useState(false)
  const [letterError, setLetterError] = useState<TranslationKey | null>(null)
  const [reportingGone, setReportingGone] = useState(false)
  // One-way: once a reader asks for the full analysis there is nothing left
  // to re-collapse, so this never goes back to `false` (see i18n's
  // `showFullReasoning` docstring — there is deliberately no paired
  // "show less" key).
  const [expanded, setExpanded] = useState(false)

  const handleReportGone = () => {
    setReportingGone(true)
    void Promise.resolve(onReportGone(match.job.id)).finally(() => setReportingGone(false))
  }

  const handleInterest = (interest: Interest) => {
    setPending((prev) => new Set(prev).add(interest))
    void Promise.resolve(onInterest(match.job.id, interest)).finally(() =>
      setPending((prev) => {
        const next = new Set(prev)
        next.delete(interest)
        return next
      }),
    )
  }

  const handleTailor = async () => {
    if (tailoring) return
    setTailoring(true)
    setTailorError(null)
    try {
      const { draft, language } = await tailorCv(match.job.id)
      const state: ApplyReviewState = {
        jobId: match.job.id,
        jobTitle: match.job.title,
        jobOrg: match.job.org,
        draft,
        language,
        kind: 'cv',
      }
      navigate('/apply/review', { state })
    } catch (reason) {
      setTailorError(applyErrorKey(reason, 'apply.errorNoCv'))
    } finally {
      setTailoring(false)
    }
  }

  const handleWriteLetter = async () => {
    if (writingLetter) return
    setWritingLetter(true)
    setLetterError(null)
    try {
      const { draft, language } = await writeCoverLetter(match.job.id)
      const state: ApplyReviewState = {
        jobId: match.job.id,
        jobTitle: match.job.title,
        jobOrg: match.job.org,
        draft,
        language,
        kind: 'letter',
      }
      navigate('/apply/review', { state })
    } catch (reason) {
      setLetterError(applyErrorKey(reason, 'apply.errorNoCvLetter'))
    } finally {
      setWritingLetter(false)
    }
  }

  // PIVOT-141: "marked not interested" used to render as the identical
  // `--brand-fill` pine pill "marked interested" does — a rejection read as
  // an endorsement unless you stopped to read the label. `--rejected` mutes
  // the whole card (see MatchCard.css), so a "no" reads as settled rather
  // than as a second kind of "yes".
  const rejected = match.interest === 'not_interested'

  const analysisLocale = resolveAnalysisLocale(match.analysis, locale)
  const analysisText = analysisFor(match.analysis, locale)
  const sentences = splitSentences(analysisText)
  // The first sentence index that must stay outside the clamp, plus every
  // sentence after it (see `findProtectFrom`'s docstring). A run-on analysis
  // with only one sentence, or one where the protected sentence IS the first
  // sentence, has nothing safe to hold back in front of it — clamping either
  // could only ever cut inside the protected content itself, the exact
  // failure this ticket's test passes caught (first the caveat mid-sentence,
  // then the caveat mid-analysis). Skip the clamp entirely rather than risk
  // that; see the module docstring. `analysisLocale`, not `locale` — the
  // pattern must match the language of `analysisText`, which the fallback
  // chain can put one step ahead of the reader's own UI language.
  const protectFrom = findProtectFrom(sentences, analysisLocale)
  const hasClampableHead = sentences.length > 1 && protectFrom > 0
  // Only clamp when there's something behind the fold to reveal — a short
  // analysis (the pre-translation fallback in MatchCard.test.tsx, e.g.) gets
  // no toggle at all rather than a control that would do nothing on click.
  const clamped = !expanded && isLongAnalysis(analysisText) && hasClampableHead
  // The protected sentences are never inside the clamped element (see
  // below) — `head` is only ever rendered when clamped, so it never needs
  // to include them. Unclamped/expanded rendering shows `analysisText`
  // whole instead.
  const head = hasClampableHead ? sentences.slice(0, protectFrom).join('') : ''
  const tail = hasClampableHead ? sentences.slice(protectFrom).join('') : ''

  return (
    <article
      className={`match-card tag-${match.tag}${rejected ? ' match-card--rejected' : ''}${featured ? ' match-card--featured' : ''}`}
    >
      <div>
        <span className="match-card-tag">
          {match.tag === 'direct' ? t('dashboard.tagDirect') : t('dashboard.tagLateral')}
        </span>
        <h3>{match.job.title}</h3>
        <p className="match-card-org">
          {match.job.org} ·{' '}
          {match.job.location?.trim() || t('dashboard.locationNotStated')}
        </p>
        {/* PIVOT-164: `job` is always the canonical -- this names whichever
            other board(s) also carry the same posting, so a slate slot never
            silently reads as one opportunity when it is really two. */}
        {match.also_on.length > 0 && (
          <p className="match-card-also-on">
            {t('dashboard.alsoOn', { sources: match.also_on.join(', ') })}
          </p>
        )}
      </div>

      <ScoreBadges
        expScore={match.exp_score}
        valuesScore={match.values_score}
        salaryScore={match.salary_score}
      />

      {/* Disclosure, not truncation (PIVOT-142, see module docstring): when
          not clamped, the full analysis is in this one <p>. When clamped,
          this <p> holds everything but the last sentence (`head`) and the
          last sentence renders in its own paragraph below, outside the
          `line-clamp` — between the two, the complete text is always in the
          DOM, so a screen reader gets all of it regardless of clamp state.
          The fade is a separate element rather than a pseudo-element on the
          <p> itself, so it can sit visually inside the clamped box without
          fighting that box's own `overflow: hidden`. */}
      <div className="match-card-analysis-wrap">
        {/* PIVOT-159: a score now carries the analysis in one language, so a
            row scored while the candidate's locale was different from
            today's UI language (or a pre-ticket row missing today's
            language entirely) shows in `analysisLocale`, not `locale`. Never
            render that silently — a Dutch reader seeing French prose with no
            explanation reads as broken, not as an honest trade-off. */}
        {analysisLocale !== locale && (
          <p className="match-card-analysis-language">
            {t('dashboard.analysisLanguageNote', {
              language: t(ANALYSIS_LANGUAGE_KEYS[analysisLocale]),
            })}
          </p>
        )}
        <p className={`match-card-analysis${clamped ? ' match-card-analysis--clamped' : ''}`}>
          {clamped ? head : analysisText}
        </p>
        {clamped && <div className="match-card-analysis-fade" aria-hidden="true" />}
      </div>
      {/* The last sentence, always rendered outside the clamped element above
          (see module docstring): a real analysis's qualifying clause —
          "salary undisclosed, not scored" and its like — lands here on
          every card, whether or not "show full reasoning" has been clicked. */}
      {clamped && <p className="match-card-analysis">{tail}</p>}
      {clamped && (
        <button
          type="button"
          className="match-card-analysis-toggle"
          onClick={() => setExpanded(true)}
        >
          {t('dashboard.showFullReasoning')}
        </button>
      )}

      {/* One action row (PIVOT-142): the quiet external link, the interest
          segmented control, then the single primary action — replacing what
          used to be a view/interest row plus a second "apply" row below it. */}
      {/* PIVOT-142 review fix: at the non-featured card width this ticket's
          own 3-up/1-up grid tiers actually give a card (~314px at 1440's
          3-column tier, ~290px at 390px — see PIVOT-124/126), the view-link
          plus a labelled segmented control plus "Tailor CV" measured ~534px
          and wrapped to three lines on every card but the one `--featured`
          one, resurfacing the exact "eleven equals, three rows" problem this
          ticket exists to fix. The label text on the view-link and the
          interest control is now screen-reader-only (`.sr-only` below) —
          the icon alone carries the meaning visually, the full word is still
          in the accessible name and in the DOM either way. Measured against
          the real fonts/tokens (a static harness loading the same
          Hanken Grotesk file and CSS values, not a guess): icon-only view
          link ~23px, icon-only segmented control ~81px, "Tailor CV" ~112px
          (up to ~146px for the longer fr/nl strings) — total comfortably
          under both 290px and 314px with the row's own two `--sp-3` gaps
          included, at every locale. */}
      <div className="match-card-actions">
        {isHttpUrl(match.job.url) ? (
          <a href={match.job.url} target="_blank" rel="noreferrer" className="job-posting-link">
            <span className="sr-only">{t('dashboard.viewPosting')}</span>
            <Icon name="arrow-out" />
          </a>
        ) : (
          // Defense-in-depth for a row already in the corpus before PIVOT-114's
          // scrape-time guard landed: no `<a>` at all, so there is nothing to
          // click and nothing for a `javascript:`/`data:` URL to execute.
          <span className="job-posting-link">{match.job.title}</span>
        )}
        <div className="match-card-interest">
          {/* PIVOT-131: `pending` used to disable both buttons for the whole
              round trip, and the only feedback was both fading to 55% opacity
              with no way to tell which one was pressed. Each button now only
              reads its own slot of `pending` — the one that was clicked marks
              itself `disabled`+`aria-busy` (which draws the accent foot-rule
              via `.btn[aria-busy='true']` in `styles/primitives.css`); the
              other stays enabled-but-inert, so the row never greys out and
              it stays unambiguous which action is in flight. */}
          <button
            type="button"
            className={`btn ${match.interest === 'interested' ? 'btn-primary' : 'btn-outline'}`}
            disabled={pending.has('interested')}
            aria-busy={pending.has('interested')}
            onClick={() => handleInterest('interested')}
          >
            <Icon name="check" />
            <span className="sr-only">
              {match.interest === 'interested'
                ? t('dashboard.interestedMarked')
                : t('dashboard.interested')}
            </span>
          </button>
          {/* PIVOT-141: `btn-primary` means "the action you would take next" —
              kept for `interested` above, but the rejected state gets
              `btn-settled` (styles/primitives.css) instead of the same pine
              fill, so a "no" reads as a settled decision, not a second kind
              of endorsement. */}
          <button
            type="button"
            className={`btn ${match.interest === 'not_interested' ? 'btn-settled' : 'btn-outline'}`}
            disabled={pending.has('not_interested')}
            aria-busy={pending.has('not_interested')}
            onClick={() => handleInterest('not_interested')}
          >
            <Icon name="cross" />
            <span className="sr-only">
              {match.interest === 'not_interested'
                ? t('dashboard.notInterestedMarked')
                : t('dashboard.notInterested')}
            </span>
          </button>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={tailoring}
          onClick={() => void handleTailor()}
        >
          <Icon name="document-edit" />
          {t('apply.tailorCta')}
        </button>
        {/* PIVOT-111: a second, `btn-outline` action beside "Tailor CV" --
            outline, not a second `btn-primary`, so the row keeps exactly one
            "the action you'd take next" (the comment above `.match-card-
            actions > .btn-primary` in MatchCard.css). `flex-wrap` on
            `.match-card-actions` already handles this row growing past a
            narrow card's width by dropping this button to its own line,
            same mechanism PIVOT-142 relies on for the row above it. */}
        <button
          type="button"
          className="btn btn-outline"
          disabled={writingLetter}
          onClick={() => void handleWriteLetter()}
        >
          <Icon name="mail" />
          {t('apply.letterCta')}
        </button>
      </div>
      {/* PIVOT-133: at the control the click happened at, not a page banner —
          the card already reverted its own `interest`, this only says why. */}
      {interestFailed && <p className="form-error">{t('common.error')}</p>}
      {tailoring && <Thinking label="apply.tailoring" patience="apply.tailoringPatience" />}
      {tailorError && <p className="form-error">{t(tailorError)}</p>}
      {writingLetter && (
        <Thinking label="apply.writingLetter" patience="apply.writingLetterPatience" />
      )}
      {letterError && <p className="form-error">{t(letterError)}</p>}
      {/* PIVOT-166: quiet and last -- the least important action on the
          card, never competing with "Tailor CV". The response never says
          whether the report actually confirmed anything (the ticket's own
          contract), so this reads as "sent", not as "removed". */}
      <button
        type="button"
        className="btn btn-ghost match-card-report-gone"
        disabled={reportedGone || reportingGone}
        aria-busy={reportingGone}
        onClick={handleReportGone}
      >
        {t(reportedGone ? 'dashboard.reportGoneSent' : 'dashboard.reportGone')}
      </button>
      {reportGoneFailed && <p className="form-error">{t('common.error')}</p>}
    </article>
  )
}
